import classNames from "classnames";
import { CSSProperties } from "react";
import {
  IWebsiteBuilderElementBase,
  IWebsiteBuilderElementDataBase,
  TWebsiteBuilderElement,
} from "./websiteBuilderElement";
import { websiteBuilderNormalizeHexColor, websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { WB_LINK_COLOR_CSS_VARIABLE, WB_LINK_HOVER_COLOR_CSS_VARIABLE } from "../../website/shared/cssVariables";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";

export interface IWebsiteBuilderLinkUrlDestination {
  type: "url";
  href: string;
  blankTarget: boolean;
}

export interface IWebsiteBuilderLinkEmailDestination {
  type: "email";
  address: string;
  subject: string;
}

export type TWebsiteBuilderLinkDestination = IWebsiteBuilderLinkUrlDestination | IWebsiteBuilderLinkEmailDestination;
export type TWebsiteBuilderLinkDestinationType = TWebsiteBuilderLinkDestination["type"];

export type TWebsiteBuilderOptionalLink = { type: "none" } | TWebsiteBuilderLinkDestination;
export type TWebsiteBuilderOptionalLinkType = TWebsiteBuilderOptionalLink["type"];

export interface IWebsiteBuilderLinkSettings {
  label: string;
  underline: boolean;
  color: string;
  hoverColor: string;
  destination: TWebsiteBuilderLinkDestination;
}

export interface IWebsiteBuilderLinkData extends IWebsiteBuilderElementDataBase {
  type: "link";
  settings: IWebsiteBuilderLinkSettings;
}

export interface IWebsiteBuilderLink extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderLinkData;
}

export function isWebsiteBuilderLink(node: TWebsiteBuilderElement): node is IWebsiteBuilderLink {
  return node?.element?.type === "link";
}

export function websiteBuilderGenerateDefaultLinkSettings(): IWebsiteBuilderLinkSettings {
  return {
    label: "Link", // TODO: Translation
    underline: false,
    color: null,
    hoverColor: null,
    destination: {
      type: "url",
      href: "",
      blankTarget: false,
    },
  };
}

export function websiteBuilderGenerateDefaultLink(): IWebsiteBuilderLink {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "link",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 40, height: 25 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 40, height: 25 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 40, height: 25 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: websiteBuilderGenerateDefaultLinkSettings(),
    },
  };
}

function websiteBuilderNormalizeLinkDestination(destination: TWebsiteBuilderLinkDestination): TWebsiteBuilderLinkDestination {
  if (destination.type === "url") {
    return {
      type: "url",
      href: websiteBuilderNormalizeValue({
        type: "string",
        value: destination.href,
        defaultValue: "",
        required: true,
      }),
      blankTarget: websiteBuilderNormalizeValue({
        type: "boolean",
        value: destination.blankTarget,
        defaultValue: false,
        required: true,
      }),
    };
  }

  return {
    type: "email",
    address: websiteBuilderNormalizeValue({
      type: "string",
      value: destination.address,
      defaultValue: "",
      required: true,
    }),
    subject: websiteBuilderNormalizeValue({
      type: "string",
      value: destination.subject,
      defaultValue: "",
      required: true,
    }),
  };
}

export function websiteBuilderNormalizeOptionalLink(link: TWebsiteBuilderOptionalLink): TWebsiteBuilderOptionalLink {
  if (link?.type === "url" || link?.type === "email") {
    return websiteBuilderNormalizeLinkDestination(link);
  }

  return { type: "none" };
}

export function websiteBuilderNormalizeLinkSettings(partialSettings: Partial<IWebsiteBuilderLinkSettings>): IWebsiteBuilderLinkSettings {
  const defaultSettings = websiteBuilderGenerateDefaultLinkSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { label, underline, color, hoverColor, destination } = partialSettings;

  let normalizedDestination: TWebsiteBuilderLinkDestination = defaultSettings.destination;

  if (destination?.type === "url" || destination?.type === "email") {
    normalizedDestination = websiteBuilderNormalizeLinkDestination(destination);
  }

  const normalizedLinkSettings: IWebsiteBuilderLinkSettings = {
    label: websiteBuilderNormalizeValue({
      type: "string",
      value: label,
      defaultValue: defaultSettings.label,
      required: true,
    }),
    underline: websiteBuilderNormalizeValue({
      type: "boolean",
      value: underline,
      defaultValue: defaultSettings.underline,
      required: true,
    }),
    color: websiteBuilderNormalizeHexColor(color, defaultSettings.color, false),
    hoverColor: websiteBuilderNormalizeHexColor(hoverColor, defaultSettings.hoverColor, false),
    destination: normalizedDestination,
  };

  return normalizedLinkSettings;
}

export function websiteBuilderGenerateLinkDesignStyles(settings: IWebsiteBuilderLinkSettings): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    ...(settings.color != null ? { [WB_LINK_COLOR_CSS_VARIABLE]: settings.color } : {}),
    ...(settings.hoverColor != null ? { [WB_LINK_HOVER_COLOR_CSS_VARIABLE]: settings.hoverColor } : {}),
  };

  let styleDeclaration = "";

  if (settings.color != null) {
    styleDeclaration += `${WB_LINK_COLOR_CSS_VARIABLE}: ${settings.color};`;
  }
  if (settings.hoverColor != null) {
    styleDeclaration += `${WB_LINK_HOVER_COLOR_CSS_VARIABLE}: ${settings.hoverColor};`;
  }

  return {
    className: classNames("website-builder-link", {
      "underline-link": settings.underline,
    }),
    styleProperties,
    styleDeclaration,
  };
}
